import React, { useState, useCallback } from 'react';
import {
  Box, Button, Chip, IconButton, Tab, Tabs, Tooltip, Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { PrintTemplateService } from '../../../service/printTemplateService';

const DOC_TYPES = [
  { value: 'invoice',           label: 'Invoice' },
  { value: 'insurance_invoice', label: 'Insurance Invoice' },
  { value: 'rx',                label: 'Prescription (Rx)' },
  { value: 'quote',             label: 'Quote' },
  { value: 'receipt',           label: 'Receipt' },
  { value: 'eye_exam_summary',  label: 'Eye Exam Summary' },
];

export default function PrintTemplateList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState(0);

  const documentType = DOC_TYPES[activeTab].value;

  const { data: templates = [], isLoading } = useQuery(
    ['printTemplates', documentType],
    () => PrintTemplateService.list(documentType).then(r => r.data || []),
    { keepPreviousData: true }
  );

  const deleteMutation = useMutation((id) => PrintTemplateService.remove(id), {
    onSuccess: (_, id) => {
      toast.success('Template deleted');
      queryClient.invalidateQueries(['printTemplates', documentType]);
    },
    onError: () => toast.error('Failed to delete template'),
  });

  const setDefaultMutation = useMutation((id) => PrintTemplateService.setDefault(id), {
    onSuccess: () => {
      toast.success('Default template updated');
      queryClient.invalidateQueries(['printTemplates', documentType]);
    },
    onError: () => toast.error('Failed to set default'),
  });

  const cloneMutation = useMutation((id) => PrintTemplateService.clone(id), {
    onSuccess: (res) => {
      toast.success('System template cloned — you can now edit it');
      queryClient.invalidateQueries(['printTemplates', documentType]);
      if (res?.data?.id) navigate(`/settings/print-templates/${res.data.id}/edit`);
    },
    onError: () => toast.error('Failed to clone template'),
  });

  const handleEdit = (t) => {
    if (!t.tenantId) {
      // System template — clone first
      cloneMutation.mutate(t.id);
    } else {
      navigate(`/settings/print-templates/${t.id}/edit`);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Print Templates</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/settings/print-templates/new', { state: { documentType } })}
        >
          New Template
        </Button>
      </Box>

      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
        {DOC_TYPES.map((dt) => (
          <Tab key={dt.value} label={dt.label} />
        ))}
      </Tabs>

      {isLoading ? (
        <Typography color="text.secondary">Loading…</Typography>
      ) : templates.length === 0 ? (
        <Typography color="text.secondary">No templates found for this document type.</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {templates.map((t) => (
            <Box
              key={t.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1.5,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              {/* Default star */}
              <Tooltip title={t.isDefault ? 'Default template' : 'Set as default'}>
                <span>
                  <IconButton
                    size="small"
                    color={t.isDefault ? 'warning' : 'default'}
                    disabled={!!t.isDefault}
                    onClick={() => setDefaultMutation.mutate(t.id)}
                  >
                    {t.isDefault ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>

              {/* Name + badges */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body1" noWrap sx={{ fontWeight: 500 }}>
                  {t.name}
                </Typography>
                {t.description && (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {t.description}
                  </Typography>
                )}
              </Box>

              {!t.tenantId && (
                <Chip label="System" size="small" color="info" variant="outlined" />
              )}
              {t.isDefault && (
                <Chip label="Default" size="small" color="warning" variant="outlined" />
              )}

              {/* Actions */}
              {!t.tenantId ? (
                <Tooltip title="Clone system template to edit">
                  <IconButton size="small" onClick={() => cloneMutation.mutate(t.id)}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                <>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => handleEdit(t)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => {
                        if (window.confirm(`Delete template "${t.name}"?`)) {
                          deleteMutation.mutate(t.id);
                        }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
